import {useState, useEffect, Suspense, lazy} from 'react';

// Lazy load the heavy Solana components only when user clicks Connect
const SolanaProviders = lazy(() =>
  import(/* webpackChunkName: "solana-providers" */ './Solana.entry.jsx')
    .then(module => ({ default: module.Solana }))
);

// Check if user was previously connected (wallet adapter stores this in localStorage)
function wasWalletPreviouslyConnected() {
  try {
    // Solana wallet adapter stores the wallet name when connected
    const walletName = localStorage.getItem('walletName');
    return walletName !== null && walletName !== '';
  } catch {
    // localStorage might not be available
    return false;
  }
}

// Lightweight placeholder button - styled to match theme's bevy-custom.css
// Uses a style tag to ensure styles aren't overridden by theme CSS
function ConnectPlaceholder({onClick, loading}) {
  return (
    <>
      <style>{`
        #solana-connect-header .lazy-wallet-placeholder {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 100% !important;
          height: auto !important;
          padding: 10px 22px !important;
          font-size: 16px !important;
          font-weight: 700 !important;
          line-height: 21.6px !important;
          letter-spacing: 0.02em !important;
          text-align: center !important;
          color: #006d53 !important;
          background-color: #0dd884 !important;
          border: 1px solid #0dd884 !important;
          border-radius: 32px !important;
          cursor: pointer !important;
          font-family: inherit !important;
          margin-bottom: 10px !important;
          text-transform: uppercase !important;
        }
        #solana-connect-header .lazy-wallet-placeholder:hover {
          background-color: #0bc876 !important;
          border-color: #0bc876 !important;
        }
        #solana-connect-header .lazy-wallet-placeholder:disabled {
          opacity: 0.8 !important;
          cursor: wait !important;
        }
      `}</style>
      <button
        className="lazy-wallet-placeholder"
        onClick={onClick}
        disabled={loading}
      >
        {loading ? 'Loading...' : 'Load Wallets'}
      </button>
    </>
  );
}

// Loading fallback shown while Solana chunk loads
function LoadingFallback() {
  return (
    <>
      <style>{`
        #solana-connect-header .lazy-wallet-loading {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 100% !important;
          padding: 10px 22px !important;
          font-size: 16px !important;
          font-weight: 700 !important;
          color: #006d53 !important;
          background-color: #0dd884 !important;
          border: 1px solid #0dd884 !important;
          border-radius: 32px !important;
          font-family: inherit !important;
          margin-bottom: 10px !important;
          text-transform: uppercase !important;
          opacity: 0.8 !important;
          cursor: wait !important;
        }
      `}</style>
      <button className="lazy-wallet-loading" disabled>
        Loading wallets...
      </button>
    </>
  );
}

export function LazyWalletButton({
  rpcServerEndpoint,
  collectionAddresses,
  burnNftBtn,
}) {
  const [activated, setActivated] = useState(false);
  const [loading, setLoading] = useState(false);

  // Auto-activate if user was previously connected (for autoConnect to work)
  useEffect(() => {
    if (wasWalletPreviouslyConnected()) {
      setLoading(true);
      setActivated(true);
    }
  }, []);

  const handleActivate = () => {
    setLoading(true);
    setActivated(true);
  };

  // Before activation, show lightweight placeholder
  if (!activated) {
    return <ConnectPlaceholder onClick={handleActivate} loading={loading} />;
  }

  // After activation, render the full Solana providers
  return (
    <Suspense fallback={<LoadingFallback />}>
      <SolanaProviders
        rpcServerEndpoint={rpcServerEndpoint}
        collectionAddresses={collectionAddresses}
        burnNftBtn={burnNftBtn}
      />
    </Suspense>
  );
}
